import clsx from "clsx";
import { useEffect, useRef } from "react";
import { useSpeechRecognition } from "react-speech-kit";
import { MicrophoneIcon, StopIcon } from "../../icon-imports";

interface AudioInputProps {
  disabled?: boolean;
  setTranscript: (transcript: string) => void;
}

const AudioInput = ({ disabled, setTranscript }: AudioInputProps) => {
  const transcriptRef = useRef("");

  const { listen, stop, listening, supported } = useSpeechRecognition({
    onResult: (transcript: string) => {
      // Update the ref to store the latest transcript
      transcriptRef.current = transcript;
      // Send transcript updates in real-time
      if (transcript && transcript.trim().length > 0) {
        setTranscript(transcript);
      }
    },
    onEnd: () => {
      // Clear the ref for next session - don't re-send transcript here
      // as onResult already handles sending it in real-time
      transcriptRef.current = "";
    },
    onError: (event: any) => {
      // Handle errors silently or show user-friendly message
      if (event.error === "not-allowed") {
        // Microphone permission denied
        console.warn("Microphone permission denied");
      }
    },
  });

  // Stop listening when component becomes disabled (e.g., when message is submitted)
  // This ensures onEnd fires and clears transcriptRef for the next session
  useEffect(() => {
    if (disabled && listening) {
      stop();
    }
  }, [disabled, listening, stop]);

  if (!supported) {
    return null;
  }

  return (
    <button
      type="button"
      disabled={disabled}
      className={clsx(
        "p-2 rounded-lg transition-colors",
        disabled
          ? "cursor-not-allowed opacity-50"
          : "hover:bg-gray-100 active:bg-gray-200"
      )}
      onClick={() => {
        if (disabled) return;

        if (listening) {
          // Stop listening - transcript was already sent via onResult
          stop();
        } else {
          // Start listening with continuous mode
          listen({
            lang: "en-US",
            continuous: true,
            interimResults: true,
          });
        }
      }}
    >
      {listening ? (
        <StopIcon
          className={clsx(
            "h-6 w-6",
            disabled ? "text-gray-400" : "text-red-700"
          )}
        />
      ) : (
        <MicrophoneIcon
          className={clsx(
            "h-6 w-6",
            disabled ? "text-gray-400" : "text-gray-500 hover:text-gray-700"
          )}
        />
      )}
    </button>
  );
};

export default AudioInput;
